import Uppy from '@uppy/core';
import { nanoid } from '@reduxjs/toolkit';
import reduxStore from '~/store';
import { UserS3UploadSignature } from '~/user/types';
import { WebEventEmitter } from '../../utils/WebEventEmitter';
import { uploadsLogger } from '../loggers';
import { RemoteFile, fileFromUrl } from './remoteFile';

import { createUppy } from '../uppy/createUppy';

import { BufferUpload, S3Upload, Upload } from '../entities/Upload';
import { selectCurrentFiles } from '../state/selectors';
import { UploaderRestrictions } from '../values/UploaderRestrictions';
import {
  FileRejection,
  validateFilesForUploads,
} from './validation/validateFilesForUpload';
import { UploadMetadata } from '../values/UploadMetadata';
import { hasMixedMediaTypes } from './validation/validationUtils/hasMixedMediaTypes';
import { UploadSource } from '../values/UploadSource';
import { LocalFile } from '../values/LocalFile';
import { UppyFile } from '../uppy/UppyFile';
import { assertUppyMetadata } from '../uppy/assertUppyMetadata';
import { BufferUploadsApi } from '../clients/BufferUploadsApi';
import { UploadProgress } from '../values/UploadProgress';
import { generateThumbnail } from './generateThumbnail';

export type UploadEventCallback = (upload: Upload) => void;
export type UploadFinishedCallback = (upload: BufferUpload) => void;

export type UploaderEvents = {
  'file-fetch-failed': (file: RemoteFile, error: Error) => void;
  'file-upload-attempted': (file: LocalFile) => void;
  'file-validation-failed': (file: LocalFile, error: Error) => void;
  'thumbnail-generated': (event: Pick<Upload, 'id' | 'thumbnailUrl'>) => void;
  'upload-prevented': (error: Error) => void;
  'upload-started': (upload: Upload) => void;
  'upload-progress': (
    event: Pick<Upload, 'id'>,
    progress: UploadProgress
  ) => void;
  'upload-stalled': (event: Pick<Upload, 'id'>) => void;
  'upload-to-s3-finished': (upload: S3Upload) => void;
  'upload-finished': UploadFinishedCallback;
  'upload-removed': (event: Pick<Upload, 'id'>) => void;
  'upload-failed': (event: Pick<Upload, 'id'>, error) => void;
  'uploader-reset': (uploaderId: string) => void;
  'uploader-closed': (uploaderId: string) => void;
};
export class Uploader extends WebEventEmitter<UploaderEvents> {
  private metadatMap: WeakMap<File, UploadMetadata> = new WeakMap();

  constructor(
    private readonly id: string,
    private readonly userId: string,
    private readonly s3UploadSignature: UserS3UploadSignature,
    private readonly uppyInstance: Uppy,
    private readonly fileRestrictions: UploaderRestrictions
  ) {
    super();

    this.mapUppyEventsToUploaderEvents();
  }

  getId(): string {
    return this.id;
  }

  // TODO UPLOADS: rename to upload and return a promise of BufferUploads
  async addFiles(
    files: File[],
    meta: Omit<UploadMetadata, 'trackingId' | 'uploaderId'>
  ): Promise<void> {
    await this.doAddFiles(files, meta)
      .then(() => this.generateThumbnails())
      .then(() => this.doUploadTos3())
      .then((uploads) => this.createUpdateDocument(uploads));
  }

  // TODO UPLOADS: check if this is a good replacement for uploadDirect
  async uploadToS3Only(
    file: File,
    meta: Omit<UploadMetadata, 'trackingId' | 'uploaderId'>
  ): Promise<S3Upload> {
    const [upload] = await this.doAddFiles([file], meta).then(() =>
      this.doUploadTos3()
    );

    this.emit('upload-to-s3-finished', upload);

    return upload;
  }

  private async doAddFiles(
    files: File[],
    meta: Omit<UploadMetadata, 'trackingId' | 'uploaderId'>
  ): Promise<void> {
    this.generateMetadata(files, meta);

    if (this.fileRestrictions.mixedMediaError && hasMixedMediaTypes(files)) {
      const error = new Error(this.fileRestrictions.mixedMediaError);
      this.emit('upload-prevented', error);

      return;
    }

    this.emitFileUploadAttempted(files);

    const { validFiles, invalidFiles } = await validateFilesForUploads(files, {
      fileRestrictions: this.fileRestrictions,
      // TODO UPLOADS: this needs to come as arguments no redux inside the uploader
      currentFiles: selectCurrentFiles(reduxStore.getState(), this.id),
    });

    this.emitFileValidationFailed(invalidFiles);

    const descriptors = validFiles.map((file) =>
      LocalFile.new(file, this.getMetadata(file))
    );

    this.uppyInstance.addFiles(descriptors);
  }

  /**
   * addFilesFromUrls
   *
   * When urls are provided instead files, we can use this method to get
   * the content of the files and add files to the uploader finally.
   */
  async addFilesFromUrls(
    remoteFiles: RemoteFile | RemoteFile[]
  ): Promise<void> {
    if (!Array.isArray(remoteFiles)) {
      remoteFiles = [remoteFiles];
    }

    const filesToDownload: Promise<File | void>[] = remoteFiles.map(
      async (remoteFile) => {
        uploadsLogger('Fetching file from url: %s', remoteFile.remoteUrl);
        return fileFromUrl(remoteFile).catch((error) => {
          this.emit('file-fetch-failed', remoteFile, error);
        });
      }
    );

    Promise.allSettled(filesToDownload).then((downloadResults) => {
      const filesDownloaded: File[] = downloadResults
        .filter(
          (result): result is PromiseFulfilledResult<File> =>
            result.status === 'fulfilled'
        )
        .map((result) => result.value);

      if (filesDownloaded?.length) {
        this.addFiles(filesDownloaded, {
          source: UploadSource.new('remote'),
          organizationId: 'todo-org-id',
          accountId: 'todo-account-id',
        });
      }
    });
  }

  getFileData(id: string): File | Blob {
    return this.uppyInstance.getFile(id).data;
  }

  reset(): void {
    this.uppyInstance.cancelAll();
    this.emit('uploader-reset', this.id);
  }

  close(cleanup: () => void): void {
    this.uppyInstance.close();
    this.emit('uploader-closed', this.id);
    this.offAll();
    cleanup();
  }

  // TODO UPLOADS: we may be fine using the uploadToS3 method and
  //               remove the complexity of having uppy children
  // @deprecated
  async uploadDirect(file: File): Promise<S3Upload> {
    uploadsLogger('Uploading file directly to S3: %s', file.name);
    const id = `${this.id}-child-${nanoid()}`;
    const childUppy = createUppy({
      id,
      userId: this.userId,
      s3UploadSignature: this.s3UploadSignature,
      fileRestrictions: this.fileRestrictions,
    });

    childUppy.on('complete', () => childUppy.close());

    const fileDescriptor = LocalFile.new(file, {
      trackingId: nanoid(),
      uploaderId: this.id,
      accountId: 'todo-account-id',
      organizationId: 'todo-org-id',
      source: UploadSource.new('direct'),
    });

    childUppy.addFile(fileDescriptor);
    const [upload] = await childUppy
      .upload<UploadMetadata>()
      .then((response) => {
        if (response.failed.length) {
          throw new Error(`Failed to upload file: ${response.failed[0].error}`);
        }

        return response.successful;
      })
      .then((files) => UppyFile.mapToS3Upload(files));

    uploadsLogger('File %s uploaded directly to S3 %s', file.name, upload.url);

    return upload;
  }

  removeUpload(upload: Upload): void {
    this.uppyInstance.removeFile(upload.id);
  }

  private emitFileValidationFailed(invalidFiles: FileRejection[]): void {
    invalidFiles.forEach(({ file, message }) => {
      this.emit(
        'file-validation-failed',
        LocalFile.new(file, this.getMetadata(file)),
        new Error(message)
      );
    });
  }

  private emitFileUploadAttempted(files: File[]): void {
    files.forEach((file) =>
      this.emit(
        'file-upload-attempted',
        LocalFile.new(file, this.getMetadata(file))
      )
    );
  }

  private async generateThumbnails(): Promise<void[]> {
    const files = this.uppyInstance.getFiles<UploadMetadata>();

    const promises = files
      .filter(UppyFile.shouldGenerateThumbnail)
      .map(async (file) => {
        return generateThumbnail(file)
          .then((thumbnail) => this.uploadDirect(thumbnail))
          .then(({ url }) => {
            this.uppyInstance.setFileState(file.id, { preview: url });
            this.emit('thumbnail-generated', {
              id: file.id,
              thumbnailUrl: url,
            });
          })
          .catch((reason) => {
            this.emit(
              'upload-failed',
              {
                id: file.id,
              },
              new Error(`Thumbnail generation failed: ${reason.message}`)
            );
            // do not try to upload if thumbnail failed
            this.uppyInstance.removeFile(file.id);
          });
      });

    return Promise.all(promises);
  }

  private async doUploadTos3(): Promise<S3Upload[]> {
    return this.uppyInstance
      .upload<UploadMetadata>()
      .then((response) => response.successful)
      .then((files) => UppyFile.mapToS3Upload(files));
  }

  private async createUpdateDocument(
    uploads: S3Upload[]
  ): Promise<BufferUpload[]> {
    const promises = uploads.map((upload) => {
      return BufferUploadsApi.create(upload);
    });

    const results = await Promise.allSettled(promises);

    return results.reduce((acc, result, index) => {
      if (result.status === 'rejected') {
        this.emit('upload-failed', uploads[index], result.reason);
      } else {
        const upload = result.value;
        this.emit('upload-finished', upload);
        acc.push(upload);
      }

      return acc;
    }, [] as BufferUpload[]);
  }

  private generateMetadata(
    files: File[],
    meta: Omit<UploadMetadata, 'uploaderId' | 'trackingId'>
  ) {
    files.forEach((file) => {
      if (this.metadatMap.has(file)) {
        // we should only generate metadata once
        uploadsLogger(
          'Warning: Metadata already exists for file: %s',
          file.name
        );
        return;
      }

      uploadsLogger('Generating metadata for file: %s', file.name);
      this.metadatMap.set(
        file,
        UploadMetadata.new({
          ...meta,
          trackingId: nanoid(),
          uploaderId: this.id,
        })
      );
    });
  }

  private getMetadata(file: File): UploadMetadata {
    const metadata = this.metadatMap.get(file);

    if (!metadata) throw new Error('Metadata not found for file');

    // Spreading into a new object because Uppy mutates the metadata in some cases
    return { ...metadata };
  }

  /**
   * Translates uppy events into uploader events.
   *
   * This hides implementation details (i.e. uppy dependency) and allows for
   * emitting directly from the uploader instance
   */
  private mapUppyEventsToUploaderEvents() {
    this.uppyInstance.on('restriction-failed', (file, error) => {
      uploadsLogger('Restriction failed for %s: %s', file?.name, error.message);

      if (!file) {
        this.emit('upload-prevented', error);

        return;
      }

      const fileData: File = file.data as File;

      const localFile = LocalFile.new(
        fileData as File,
        this.getMetadata(fileData as File)
      );

      this.emit('file-validation-failed', localFile, error);
    });

    this.uppyInstance.on('upload-error', (file, error) => {
      this.emit('upload-failed', { id: file.id }, error);
    });

    this.uppyInstance.on('file-removed', (file) => {
      this.emit('upload-removed', { id: file.id });
    });

    this.uppyInstance.on('file-added', (file) => {
      assertUppyMetadata(file);
      const upload = UppyFile.toUpload(file);
      this.emit('upload-started', upload);
    });

    this.uppyInstance.on('upload-progress', (file, uppyProgress) => {
      const progress = UploadProgress.new(
        uppyProgress.bytesTotal,
        uppyProgress.bytesUploaded
      );
      this.emit('upload-progress', { id: file.id }, progress);
    });
  }

  /**
   * @deprecated
   *
   * We should hide uppy as an implementation detail.
   * Uppy events can be translated into our own uploader events.
   * This method could still be useful for testing
   */
  getUppyInstance(): Uppy {
    return this.uppyInstance;
  }
}
